import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { request } from '../api/client';
import { DataTable, type Column } from '../components/DataTable';
import { selecaoPadraoDoTurno } from './reprocessar-selecao';

interface ConversaItem {
  id: string;
  clienteNome: string;
  telefone: string;
  pausada: boolean;
  intervencaoHumana: boolean;
  flagIntervencao: string | null;
  ultimaMensagemEm: string | null;
  ticketsAbertos: number;
  validada: boolean;
  /** Sara responde curto e sempre volta pras cartas nesta conversa. */
  modoObjetivo: boolean;
  acessoMapaManual: boolean;
  temCompraMapa: boolean;
  temAcessoMapa: boolean;
  creditosTiragem: number;
  primeiraCompra: string | null;
  liberadoManualEm: string | null;
  dataAquisicao: string | null;
  origemAquisicao: 'compra' | 'liberacao_manual' | null;
  diasDesdeAquisicao: number | null;
  foraJanela: boolean;
  /** Instância inglesa. `null` no Brasil, e é assim que a tela sabe que não
   *  deve desenhar as colunas do Garden. */
  acessoSe1Em: string | null;
  diasDesdeSe1: number | null;
  foraJanelaGarden: boolean;
  temGarden: boolean;
  gardenManual: boolean;
  produtosApp: string[] | null;
}

interface ConversaPage {
  items: ConversaItem[];
  total: number;
  page: number;
  pageSize: number;
}

type Filter =
  | 'todas'
  | 'liberadas'
  | 'nao_liberadas'
  | 'pausadas'
  | 'humano'
  | 'ticket_aberto'
  | 'fora_janela';

export function ConversasPage(): JSX.Element {
  const [filter, setFilter] = useState<Filter>('todas');
  /** Conversa cujo turno está aberto pra seleção no modal "Responder de novo". */
  const [reprocessarAlvo, setReprocessarAlvo] = useState<{ id: string; nome: string } | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const queryClient = useQueryClient();

  // Debounce simples: 350ms após parar de digitar.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Volta pra página 1 quando filtro/busca/tamanho mudam, senão fica em
  // página vazia se o total novo for menor.
  useEffect(() => {
    setPage(1);
  }, [filter, search, pageSize]);

  const params = new URLSearchParams();
  if (filter === 'pausadas') params.set('pausada', 'true');
  else if (filter === 'humano') params.set('intervencaoHumana', 'true');
  else if (filter === 'ticket_aberto') params.set('ticketAberto', 'true');
  else if (filter === 'liberadas') params.set('validada', 'true');
  else if (filter === 'nao_liberadas') params.set('validada', 'false');
  else if (filter === 'fora_janela') params.set('foraJanela', 'true');
  if (search) params.set('search', search);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  const qs = `?${params.toString()}`;

  const { data, isLoading, isError, refetch } = useQuery<ConversaPage>({
    queryKey: ['conversas', filter, search, page, pageSize],
    queryFn: () => request<ConversaPage>(`/admin/conversas${qs}`),
    refetchInterval: 30_000,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  const assumir = useMutation({
    mutationFn: (id: string) => request(`/admin/conversas/${id}/assumir`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversas'] }),
  });

  const devolver = useMutation({
    mutationFn: (id: string) => request(`/admin/conversas/${id}/devolver`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversas'] }),
  });

  const liberarAcesso = useMutation({
    mutationFn: (id: string) =>
      request(`/admin/conversas/${id}/liberar-acesso`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversas'] }),
  });

  const forcarResposta = useMutation({
    mutationFn: (id: string) =>
      request<{ ok: true; balloonsEnfileirados: number; bypassUntil: string | null }>(
        `/admin/conversas/${id}/forcar-resposta`,
        { method: 'POST' },
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['conversas'] });
      alert(
        `Sara vai processar ${res.balloonsEnfileirados} mensagem(ns) pendente(s) agora. Daily limit liberado até virar o dia BR.`,
      );
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Não consegui forçar a resposta: ${msg}`);
    },
  });

  /**
   * "Responder de novo": a Sara JÁ respondeu, mas respondeu mal. Refaz o último
   * turno da cliente. O backend recusa quando aquela resposta debitou crédito —
   * reprocessar ali cobraria a cliente duas vezes.
   */
  const responderDeNovo = useMutation({
    mutationFn: ({ id, mensagemIds }: { id: string; mensagemIds: string[] }) =>
      request<{ ok: true; balloonsEnfileirados: number; bypassUntil: string | null }>(
        `/admin/conversas/${id}/forcar-resposta`,
        { method: 'POST', body: { reprocessar: true, mensagemIds } },
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['conversas'] });
      setReprocessarAlvo(null);
      alert(
        `Sara vai reler ${res.balloonsEnfileirados} mensagem(ns) da cliente e responder. Chega em alguns segundos.`,
      );
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Não deu pra responder de novo: ${msg}`);
    },
  });

  const continuarResposta = useMutation({
    mutationFn: (id: string) =>
      request<{ ok: true; balloonsEnfileirados: number; promiseType: string }>(
        `/admin/conversas/${id}/continuar-resposta`,
        { method: 'POST' },
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['conversas'] });
      alert(
        `Recovery executado! Sara vai entregar ${res.balloonsEnfileirados} balão(ões) sobre ${res.promiseType}.`,
      );
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Recovery não rolou: ${msg}`);
    },
  });

  const editarNome = useMutation({
    mutationFn: ({ id, primeiroNome }: { id: string; primeiroNome: string }) =>
      request<{ ok: true; primeiroNome: string }>(`/admin/conversas/${id}/nome-cliente`, {
        method: 'POST',
        body: { primeiroNome },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversas'] }),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Não consegui salvar o nome: ${msg}`);
    },
  });

  const ajustarCreditos = useMutation({
    mutationFn: ({
      id,
      valor,
      delta,
      motivo,
    }: {
      id: string;
      valor?: number;
      delta?: number;
      motivo?: string;
    }) =>
      request<{ creditosTiragem: number }>(`/admin/conversas/${id}/creditos`, {
        method: 'POST',
        body: { valor, delta, motivo },
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['conversas'] });
      alert(`Saldo atualizado: ${res.creditosTiragem} crédito(s) de tiragem.`);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Não consegui ajustar créditos: ${msg}`);
    },
  });

  const sinalizarPagamento = useMutation({
    mutationFn: ({
      id,
      quantidade,
      mensagemProativa,
    }: {
      id: string;
      quantidade: number;
      mensagemProativa?: string;
    }) =>
      request<{ creditosTiragem: number; balloonsEnfileirados: number }>(
        `/admin/conversas/${id}/sinalizar-pagamento`,
        { method: 'POST', body: { quantidade, mensagemProativa } },
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['conversas'] });
      alert(
        `✅ Pagamento sinalizado! Saldo agora: ${res.creditosTiragem} crédito(s). Sara vai mandar mensagem proativa.`,
      );
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Não consegui sinalizar pagamento: ${msg}`);
    },
  });

  const toggleGardenManual = useMutation({
    mutationFn: ({ id, gardenManual }: { id: string; gardenManual: boolean }) =>
      request<{ ok: true; gardenManual: boolean }>(`/admin/conversas/${id}/garden-manual`, {
        method: 'POST',
        body: { gardenManual },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversas'] }),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Não consegui confirmar o Garden: ${msg}`);
    },
  });

  const toggleModoObjetivo = useMutation({
    mutationFn: ({ id, modoObjetivo }: { id: string; modoObjetivo: boolean }) =>
      request<{ ok: true; modoObjetivo: boolean }>(`/admin/conversas/${id}/modo-objetivo`, {
        method: 'POST',
        body: { modoObjetivo },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversas'] }),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Não consegui mudar o modo objetivo: ${msg}`);
    },
  });

  const toggleAcessoMapa = useMutation({
    mutationFn: ({ id, acessoMapaManual }: { id: string; acessoMapaManual: boolean }) =>
      request<{ ok: true; acessoMapaManual: boolean }>(`/admin/conversas/${id}/acesso-mapa`, {
        method: 'POST',
        body: { acessoMapaManual },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversas'] }),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Não consegui ajustar acesso ao mapa: ${msg}`);
    },
  });

  const abrirChatwoot = async (conversaId: string) => {
    try {
      const { url } = await request<{ url: string | null }>(
        `/admin/conversas/${conversaId}/chatwoot-link`,
      );
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Chatwoot não configurado ou contato não encontrado.');
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao abrir conversa no Chatwoot. Tenta de novo.');
    }
  };

  // A instância inglesa não tem crédito de tiragem avulsa e tem Garden. Basta
  // uma linha trazer os campos novos para saber onde estamos: o backend manda
  // `null` neles no Brasil.
  const ehIngles = (data?.items ?? []).some(
    (r) => r.produtosApp !== null || r.acessoSe1Em !== null,
  );

  const columns: Column<ConversaItem>[] = [
    {
      key: 'clienteNome',
      header: 'Cliente',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Link
            to={`/conversas/${row.id}`}
            className="text-sara-accent hover:underline text-xs"
            title="Abrir chat completo"
            aria-label={`Abrir conversa de ${row.clienteNome}`}
          >
            💬
          </Link>
          <NomeClienteInline
            conversaId={row.id}
            nomeAtual={row.clienteNome}
            onSave={(novo) => editarNome.mutate({ id: row.id, primeiroNome: novo })}
            saving={editarNome.isPending}
          />
        </div>
      ),
    },
    { key: 'telefone', header: 'Telefone' },
    {
      key: 'flagIntervencao',
      header: 'Status',
      render: (row) => {
        // Sem repetir "Sara" em toda linha: a tela inteira é sobre ela, e a
        // palavra só roubava largura de uma tabela que já não cabia.
        if (row.flagIntervencao === 'risco_ativo')
          return <span className="text-sara-danger">Risco ativo</span>;
        if (row.pausada) return <span className="text-sara-warning">pausada</span>;
        return <span className="text-sara-success">atendendo</span>;
      },
    },
    {
      key: 'validada',
      header: 'Acesso',
      render: (row) =>
        row.validada ? (
          <span className="text-xs text-sara-success">Liberado</span>
        ) : (
          <button
            type="button"
            onClick={() => {
              if (
                confirm(
                  `Liberar acesso da Sara pra ${row.clienteNome} (${row.telefone})?\n\nVai mandar uma mensagem proativa pro WhatsApp avisando que o acesso foi liberado.`,
                )
              ) {
                liberarAcesso.mutate(row.id);
              }
            }}
            disabled={liberarAcesso.isPending}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sara-warning/15 text-sara-warning hover:bg-sara-warning/25 transition disabled:opacity-50"
            title="Cliente sem token/email validado. Clique pra liberar manualmente — Sara manda mensagem pro cliente."
          >
            Liberar
          </button>
        ),
    },
    {
      key: 'ultimaMensagemEm',
      header: 'Última msg',
      render: (row) =>
        row.ultimaMensagemEm ? new Date(row.ultimaMensagemEm).toLocaleString('pt-BR') : '—',
    },
    {
      key: 'modoObjetivo',
      header: 'Objetivo',
      render: (row) => (
        <button
          type="button"
          role="switch"
          aria-checked={row.modoObjetivo}
          aria-label={
            row.modoObjetivo
              ? `Desligar o modo objetivo de ${row.clienteNome}`
              : `Ligar o modo objetivo de ${row.clienteNome}`
          }
          onClick={() => toggleModoObjetivo.mutate({ id: row.id, modoObjetivo: !row.modoObjetivo })}
          disabled={toggleModoObjetivo.isPending}
          className="toggle"
          data-state={row.modoObjetivo ? 'on' : 'off'}
          title={
            row.modoObjetivo
              ? 'Modo objetivo LIGADO: Sara responde curto, não espelha carinho e traz a conversa de volta pras cartas.'
              : 'Ligue quando a conversa virar papo livre sem leitura: Sara passa a responder curto e a oferecer tiragem em vez de conversar de graça.'
          }
        >
          <span className="toggle-thumb" aria-hidden="true" />
        </button>
      ),
    },
    {
      key: 'acessoMapaManual',
      header: 'Mapa',
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.temCompraMapa && (
            <span
              className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-400"
              title="Cliente comprou o Mapa Astral na Kiwify (Compra upsell1 aprovada). Sara já libera o mapa — não precisa do toggle manual."
            >
              ✓ Kiwify
            </span>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={row.acessoMapaManual}
            aria-label={
              row.acessoMapaManual
                ? 'Desligar acesso manual ao Mapa Astral'
                : 'Liberar Mapa Astral manualmente (sem Compra Kiwify)'
            }
            onClick={() =>
              toggleAcessoMapa.mutate({
                id: row.id,
                acessoMapaManual: !row.acessoMapaManual,
              })
            }
            disabled={toggleAcessoMapa.isPending}
            className="toggle"
            data-state={row.acessoMapaManual ? 'on' : 'off'}
            title={
              row.temCompraMapa
                ? 'Cliente já tem acesso ao Mapa via Compra Kiwify. Este toggle é só um override manual extra — não precisa ligar.'
                : row.acessoMapaManual
                  ? 'Acesso ao Mapa Astral liberado manualmente — Sara pode coletar/falar do mapa'
                  : 'Sem acesso ao Mapa Astral. Sara só fala se cliente tiver Compra(upsell1) ou se você ligar aqui'
            }
          >
            <span className="toggle-thumb" aria-hidden="true" />
          </button>
        </div>
      ),
    },
    {
      key: 'aquisicao',
      header: 'Adquirida há',
      render: (row) => {
        // INSTÂNCIA INGLESA. O relógio aqui é OUTRO: não existe compra Kiwify, e
        // quem decide a oferta do Garden é a data em que o acesso à Semente
        // começou, que o app manda no payload. Sem este ramo a coluna ficava
        // vazia justamente na instância onde a janela existe.
        if (row.acessoSe1Em !== null || row.produtosApp !== null) {
          if (!row.acessoSe1Em || row.diasDesdeSe1 == null) {
            return (
              <span
                className="text-xs text-sara-text-subtle"
                title="O app ainda não contou quando o acesso à Semente começou (ou ela nunca comprou a Semente). Sem essa data a Sara não oferta: ela segura a leitura e abre chamado."
              >
                —
              </span>
            );
          }
          const dataSe1 = new Date(row.acessoSe1Em).toLocaleDateString('pt-BR');
          const diasLabel =
            row.diasDesdeSe1 === 0
              ? 'hoje'
              : row.diasDesdeSe1 === 1
                ? '1 dia'
                : `${row.diasDesdeSe1} dias`;
          const dentro = !row.foraJanelaGarden;
          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                dentro
                  ? 'bg-sara-success/15 text-sara-success'
                  : 'bg-sara-warning/15 text-sara-warning'
              }`}
              title={`Semente adquirida em ${dataSe1}, ${diasLabel} atrás. ${
                dentro
                  ? 'Dentro dos 30 dias: a tiragem específica continua incluída.'
                  : 'Fora dos 30 dias: a tiragem específica é do Abundance Garden.'
              }`}
            >
              {dentro ? '✓' : '⏰'} {diasLabel} · {dentro ? 'dentro 30d' : 'fora 30d'}
            </span>
          );
        }
        if (!row.dataAquisicao || row.diasDesdeAquisicao == null) {
          return (
            <span
              className="text-xs text-sara-text-subtle"
              title="Cliente sem Compra Kiwify nem liberação manual registrada. PaywallService libera grátis (caso raro)."
            >
              —
            </span>
          );
        }
        const data = new Date(row.dataAquisicao);
        const dataStr = data.toLocaleDateString('pt-BR');
        const origem =
          row.origemAquisicao === 'compra' ? 'compra Kiwify' : 'liberação manual pelo admin';
        const diasLabel =
          row.diasDesdeAquisicao === 0
            ? 'hoje'
            : row.diasDesdeAquisicao === 1
              ? '1 dia'
              : `${row.diasDesdeAquisicao} dias`;
        const isWithin = !row.foraJanela;
        const colorCls = isWithin
          ? 'bg-sara-success/15 text-sara-success'
          : 'bg-sara-warning/15 text-sara-warning';
        const icon = isWithin ? '✓' : '⏰';
        const statusLabel = isWithin
          ? `dentro 7d (faltam ${Math.max(0, 7 - row.diasDesdeAquisicao)})`
          : 'fora 7d';
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorCls}`}
            title={`Adquirida em ${dataStr} (${origem}) — ${diasLabel} atrás. ${
              isWithin
                ? 'Dentro dos 7 dias gratuitos.'
                : 'Fora da janela — tiragens específicas precisam crédito.'
            }`}
          >
            {icon} {diasLabel} · {statusLabel}
          </span>
        );
      },
    },
    {
      key: 'creditos',
      // No INGLÊS esta coluna vira "Garden": não existe crédito de tiragem
      // avulsa ali (a instância inteira roda com a cobrança por leitura
      // desligada), então o número era sempre 0 e ocupava espaço sem dizer nada.
      // O que a dona precisa ver é quem assina.
      header: ehIngles ? 'Garden' : 'Créditos',
      render: (row) =>
        row.produtosApp !== null || row.acessoSe1Em !== null ? (
          <div className="flex items-center gap-1">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                row.temGarden
                  ? 'bg-sara-success/15 text-sara-success'
                  : 'bg-sara-bg-soft text-sara-text-subtle'
              }`}
              title={
                row.temGarden
                  ? row.gardenManual
                    ? 'Garden confirmado À MÃO no painel. A Sara não oferta e não segura a leitura desta cliente. Isto NÃO abre as telas do Garden no app: para isso, o painel do app.'
                    : 'Assina o Abundance Garden (veio do app). Tiragem ilimitada no chat.'
                  : 'Não assina o Garden. Passados os 30 dias da Semente, recebe a oferta.'
              }
            >
              {row.temGarden ? (row.gardenManual ? '🌱 à mão' : '🌱 sim') : 'não'}
            </span>
            <button
              type="button"
              onClick={() => {
                const ligar = !row.gardenManual;
                const texto = ligar
                  ? `Confirmar o Garden de ${row.clienteNome} À MÃO?\n\nUse quando a cliente assinou de verdade e a Sara não reconheceu. Confira na DigiStore antes.\n\nA partir daqui a Sara para de ofertar e entrega a tiragem sem cobrar.\n\n⚠️ Isto NÃO abre as telas do Garden no app (oráculo, leitura do mês, leitura da mão). Para isso, use o painel do app.`
                  : `Desfazer a confirmação manual do Garden de ${row.clienteNome}?\n\nSe o app disser que ela assina, ela continua com acesso mesmo assim.`;
                if (!confirm(texto)) return;
                toggleGardenManual.mutate({ id: row.id, gardenManual: ligar });
              }}
              disabled={toggleGardenManual.isPending}
              className="text-xs px-1.5 py-0.5 rounded bg-sara-bg-soft hover:bg-sara-bg-hover disabled:opacity-50"
              title="Confirmar (ou desfazer) o Garden à mão, quando o reconhecimento automático falhar"
            >
              {row.gardenManual ? '↺' : '💰'}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                row.creditosTiragem > 0
                  ? 'bg-sara-success/15 text-sara-success'
                  : row.foraJanela
                    ? 'bg-sara-danger/15 text-sara-danger'
                    : 'bg-sara-bg-soft text-sara-text-subtle'
              }`}
              title="Créditos de tiragem avulsa (R$11,77 cada). Cliente fora dos 7 dias precisa pra fazer tiragem específica."
            >
              {row.creditosTiragem}
            </span>
            <button
              type="button"
              onClick={() => {
                const raw = prompt(
                  `Ajustar créditos de ${row.clienteNome}\n\nSaldo atual: ${row.creditosTiragem}\n\nExemplos:\n  +1  → adiciona 1 (vira ${row.creditosTiragem + 1})\n  -1  → subtrai 1 (vira ${Math.max(0, row.creditosTiragem - 1)})\n  =0  → ZERA o saldo\n  =5  → seta direto pra 5\n\n⚠️ Digitar só "0" NÃO zera (fica ${row.creditosTiragem}). Pra zerar use =0.`,
                  '+1',
                );
                if (raw === null) return;
                const trimmed = raw.trim();
                if (!trimmed) return;
                const motivo = prompt('Motivo (opcional):', '') ?? '';
                if (trimmed.startsWith('=')) {
                  const valor = parseInt(trimmed.slice(1), 10);
                  if (Number.isNaN(valor) || valor < 0) {
                    alert('Valor inválido. Use número não-negativo.');
                    return;
                  }
                  ajustarCreditos.mutate({ id: row.id, valor, motivo });
                } else {
                  const delta = parseInt(trimmed, 10);
                  if (Number.isNaN(delta)) {
                    alert('Delta inválido. Use número (ex: +1, -2).');
                    return;
                  }
                  ajustarCreditos.mutate({ id: row.id, delta, motivo });
                }
              }}
              disabled={ajustarCreditos.isPending}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-sara-bg-soft text-sara-text-muted hover:text-sara-text hover:bg-sara-border transition disabled:opacity-50"
              title="Ajustar créditos manualmente (corrigir bug, adicionar grátis, etc)"
              aria-label="Ajustar créditos"
            >
              ⚙
            </button>
          </div>
        ),
    },
    {
      key: 'forcarResposta',
      header: 'Forçar',
      render: (row) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              if (
                confirm(
                  `Forçar a Sara responder agora as mensagens pendentes de ${row.clienteNome}?\n\nÚtil quando ela silenciou por daily limit ou ficou sem responder por algum motivo. Vai liberar o daily limit até virar o dia BR.`,
                )
              ) {
                forcarResposta.mutate(row.id);
              }
            }}
            disabled={row.pausada || forcarResposta.isPending}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sara-primary/15 text-sara-primary hover:bg-sara-primary/25 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              row.pausada
                ? 'Não dá pra forçar enquanto humano tá atendendo (Sara pausada)'
                : 'Forçar Sara processar mensagens pendentes agora — útil se ela silenciou por daily limit'
            }
          >
            ▶ Forçar
          </button>
          <button
            type="button"
            onClick={() => setReprocessarAlvo({ id: row.id, nome: row.clienteNome })}
            disabled={row.pausada || responderDeNovo.isPending}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-700 hover:bg-amber-500/25 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              row.pausada
                ? 'Não dá pra reprocessar enquanto humano tá atendendo (Sara pausada)'
                : 'Responder de novo — refaz o último turno quando a Sara respondeu mal'
            }
          >
            ↻ De novo
          </button>
          <button
            type="button"
            onClick={() => {
              if (
                confirm(
                  `Continuar resposta da Sara pra ${row.clienteNome}?\n\nUsa quando a Sara prometeu uma carta/leitura ("Vou puxar uma carta. Um segundinho.") e ficou parada sem entregar. Backend identifica o que foi prometido e cumpre — sem precisar de mensagem nova da cliente.`,
                )
              ) {
                continuarResposta.mutate(row.id);
              }
            }}
            disabled={row.pausada || continuarResposta.isPending}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sara-accent/15 text-sara-accent hover:bg-sara-accent/25 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              row.pausada
                ? 'Não dá pra continuar enquanto humano tá atendendo (Sara pausada)'
                : 'Continuar resposta — útil quando Sara prometeu carta/leitura e não entregou'
            }
          >
            ↻ Continuar
          </button>
          <button
            type="button"
            onClick={() => {
              const raw = prompt(
                `Sinalizar quantas tiragens pagas pra ${row.clienteNome}?\n\nUsado quando cliente paga fora da Kiwify (Pix manual, transferência, etc) ou quando webhook falhou. Adiciona N créditos + Sara abre proativa explicando.\n\nQuantidade (1-20):`,
                '1',
              );
              if (raw === null) return;
              const quantidade = parseInt(raw.trim(), 10);
              if (Number.isNaN(quantidade) || quantidade < 1 || quantidade > 20) {
                alert('Quantidade inválida (1-20).');
                return;
              }
              const msg = prompt('Mensagem proativa (deixe vazio pra usar a default):', '');
              sinalizarPagamento.mutate({
                id: row.id,
                quantidade,
                ...(msg?.trim() ? { mensagemProativa: msg.trim() } : {}),
              });
            }}
            disabled={row.pausada || sinalizarPagamento.isPending}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sara-success/15 text-sara-success hover:bg-sara-success/25 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              row.pausada
                ? 'Não dá pra sinalizar pagamento enquanto humano atende'
                : 'Sinalizar pagamento de N tiragens — adiciona créditos + Sara abre proativa'
            }
          >
            💰 Pago
          </button>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Sara',
      render: (row) => (
        <button
          type="button"
          role="switch"
          aria-checked={!row.pausada}
          aria-label={row.pausada ? 'Reativar atendimento da Sara' : 'Pausar atendimento da Sara'}
          onClick={() => (row.pausada ? devolver.mutate(row.id) : assumir.mutate(row.id))}
          disabled={assumir.isPending || devolver.isPending}
          className="toggle"
          data-state={row.pausada ? 'off' : 'on'}
          title={
            row.pausada
              ? 'Sara está pausada — clique pra reativar'
              : 'Sara está atendendo — clique pra pausar (humano assume)'
          }
        >
          <span className="toggle-thumb" aria-hidden="true" />
        </button>
      ),
    },
    {
      key: 'chatwoot',
      header: 'Chatwoot',
      render: (row) => (
        <button
          type="button"
          onClick={() => abrirChatwoot(row.id)}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sara-accent/15 text-sara-accent hover:bg-sara-accent/25 transition"
          title="Abre a conversa privada da pessoa no Chatwoot em nova aba"
        >
          Abrir 💬
        </button>
      ),
    },
  ];

  return (
    <section aria-labelledby="conversas-heading" className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 id="conversas-heading" className="h1">
            Conversas
          </h1>
          <p className="muted text-sm mt-1">
            Histórico das interações da Sara com clientes via WhatsApp.
          </p>
        </div>
        <a
          href="https://chat.tarot.suportesam.com/app/accounts/1/dashboard"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ghost"
          title="Abrir o painel do Chatwoot em uma nova aba pra acompanhar / atender humanamente"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
          Abrir no Chatwoot
        </a>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <fieldset
          className="flex flex-wrap gap-2"
          role="radiogroup"
          aria-label="Filtro de conversas"
        >
          {(
            [
              'todas',
              'liberadas',
              'nao_liberadas',
              'pausadas',
              'humano',
              'ticket_aberto',
              'fora_janela',
            ] as Filter[]
          ).map((f) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={filter === f}
              onClick={() => setFilter(f)}
              className={`btn-ghost text-xs ${
                filter === f ? 'bg-sara-accent/10 border-sara-accent text-sara-accent' : ''
              }`}
            >
              {f === 'todas'
                ? 'Todas'
                : f === 'liberadas'
                  ? 'Só liberadas'
                  : f === 'nao_liberadas'
                    ? 'Aguardando liberação'
                    : f === 'pausadas'
                      ? 'Pausadas'
                      : f === 'humano'
                        ? 'Com humano'
                        : f === 'ticket_aberto'
                          ? 'Com ticket aberto'
                          : 'Fora dos 7 dias'}
            </button>
          ))}
        </fieldset>
        <div className="flex-1 min-w-[200px] max-w-md">
          <label htmlFor="conv-search" className="sr-only">
            Buscar por nome, telefone ou email
          </label>
          <input
            id="conv-search"
            type="search"
            placeholder="🔎 Buscar por nome, telefone ou email…"
            className="input text-sm w-full"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        {data && (
          <span className="text-xs text-sara-text-subtle">
            {data.total} conversa{data.total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={data?.items}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        rowKey={(r) => r.id}
        emptyMessage="Nenhuma conversa encontrada com esse filtro."
        dense
      />

      {data && data.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-4 text-sm text-sara-text-muted">
          <div className="flex items-center gap-2">
            <span>Por página:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="bg-sara-bg-soft border border-sara-border rounded px-2 py-0.5 text-sara-text"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span>
              Página {page} de {totalPages} · {data.total} conversa{data.total === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              disabled={page <= 1 || isLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 rounded border border-sara-border hover:bg-sara-bg-soft disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Anterior
            </button>
            <button
              type="button"
              disabled={page >= totalPages || isLoading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1 rounded border border-sara-border hover:bg-sara-bg-soft disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Próxima →
            </button>
          </div>
        </div>
      )}

      {reprocessarAlvo && (
        <ReprocessarTurnoModal
          conversaId={reprocessarAlvo.id}
          nome={reprocessarAlvo.nome}
          enviando={responderDeNovo.isPending}
          onCancelar={() => setReprocessarAlvo(null)}
          onConfirmar={(mensagemIds) =>
            responderDeNovo.mutate({ id: reprocessarAlvo.id, mensagemIds })
          }
        />
      )}
    </section>
  );
}

/**
 * Nome do cliente editável inline. Click pra entrar em modo edição,
 * Enter ou blur salva, Esc cancela. Usado pra corrigir nome quando a
 * heurística pegou errado ("Ser" de "Pode ser Paty") ou a Kiwify enviou
 * nome incompleto.
 */
function NomeClienteInline({
  conversaId,
  nomeAtual,
  onSave,
  saving,
}: {
  conversaId: string;
  nomeAtual: string;
  onSave: (novo: string) => void;
  saving: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(nomeAtual);

  // Resincroniza quando a linha re-renderiza com nome novo do server.
  useEffect(() => {
    if (!editing) setDraft(nomeAtual);
  }, [nomeAtual, editing, conversaId]);

  function commit(): void {
    const trimmed = draft.trim();
    setEditing(false);
    if (trimmed && trimmed !== nomeAtual) {
      onSave(trimmed);
    } else {
      setDraft(nomeAtual);
    }
  }

  if (editing) {
    return (
      <input
        type="text"
        value={draft}
        autoFocus
        maxLength={50}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditing(false);
            setDraft(nomeAtual);
          }
        }}
        className="bg-sara-bg-soft border border-sara-border rounded px-1.5 py-0.5 text-sm w-full max-w-[160px]"
        aria-label="Editar nome do cliente"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-left hover:text-sara-primary hover:underline decoration-dotted underline-offset-2"
      title="Clique pra editar o nome pelo qual a Sara chama essa cliente"
    >
      {nomeAtual}
    </button>
  );
}

interface TurnoPreview {
  ok: boolean;
  mensagens: Array<{ id: string; conteudo: string; temImagem?: boolean }>;
  respostasSubstituidas: number;
  motivo: string | null;
}

/**
 * Modal do "Responder de novo": mostra as falas da cliente no último turno e
 * deixa o operador escolher QUAIS a Sara vai reler.
 *
 * Por que escolher, e não mandar o turno todo: numa rajada, parte do pedido
 * pode ter sido bem respondida. A Fernanda (30/07/2026) mandou "bom dia" +
 * "pode tirar a carta do dia?" + "queria ver dois sonhos" — a carta saiu certa,
 * só os sonhos ficaram sem resposta. Reler tudo faria a Sara mandar uma SEGUNDA
 * carta no mesmo dia.
 *
 * Padrão: só a ÚLTIMA marcada. É o caso comum (a cliente pergunta uma coisa e a
 * Sara erra), e deixa o operador MARCAR mais em vez de ter que desmarcar.
 */
function ReprocessarTurnoModal({
  conversaId,
  nome,
  enviando,
  onCancelar,
  onConfirmar,
}: {
  conversaId: string;
  nome: string;
  enviando: boolean;
  onCancelar: () => void;
  onConfirmar: (mensagemIds: string[]) => void;
}) {
  const [marcados, setMarcados] = useState<string[]>([]);

  const { data, isLoading, error } = useQuery<TurnoPreview>({
    queryKey: ['turno-reprocessavel', conversaId],
    queryFn: () => request<TurnoPreview>(`/admin/conversas/${conversaId}/turno-reprocessavel`),
    staleTime: 0,
  });

  const mensagens = data?.mensagens ?? [];
  /** Identidade do turno: muda só quando a lista de falas muda de verdade. */
  const chaveDoTurno = mensagens.map((m) => m.id).join(',');

  useEffect(() => {
    setMarcados(selecaoPadraoDoTurno(chaveDoTurno ? chaveDoTurno.split(',') : []));
  }, [chaveDoTurno]);

  const alterna = (id: string) =>
    setMarcados((atual) => (atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Responder de novo para ${nome}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancelar();
      }}
    >
      <div className="bg-sara-bg border border-sara-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-sara-border">
          <h2 className="text-base font-semibold">Responder de novo — {nome}</h2>
          <p className="text-sm text-sara-muted mt-1">
            Marque o que a Sara deve reler. Deixe de fora o que ela já respondeu bem, senão ela
            entrega de novo.
          </p>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {isLoading && <p className="text-sm text-sara-muted">Carregando o turno…</p>}

          {error && (
            <p className="text-sm text-red-600">
              Não consegui ler o turno: {error instanceof Error ? error.message : 'erro'}
            </p>
          )}

          {data && !data.ok && (
            <p className="text-sm text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded p-3">
              {data.motivo}
            </p>
          )}

          {data?.ok && (
            <>
              <ul className="space-y-2">
                {mensagens.map((m) => (
                  <li key={m.id}>
                    <label className="flex gap-3 items-start p-2 rounded hover:bg-sara-bg-soft cursor-pointer">
                      <input
                        type="checkbox"
                        checked={marcados.includes(m.id)}
                        onChange={() => alterna(m.id)}
                        className="mt-1 h-4 w-4 shrink-0"
                      />
                      <span className="text-sm whitespace-pre-wrap break-words">
                        {/* Print sem legenda é uma linha VAZIA sem isto, e o
                            operador não marca o que não vê (caso Terri). */}
                        {m.temImagem && (
                          <span className="text-sara-muted">
                            🖼️ imagem{m.conteudo.trim() ? ' + ' : ''}
                          </span>
                        )}
                        {m.conteudo}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              {data.respostasSubstituidas > 0 && (
                <p className="text-xs text-sara-muted mt-3">
                  {data.respostasSubstituidas} resposta(s) da Sara continuam no histórico — a nova
                  entra depois delas, não apaga nada.
                </p>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-sara-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancelar}
            className="px-3 py-1.5 rounded-lg text-sm border border-sara-border hover:bg-sara-bg-soft"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirmar(marcados)}
            disabled={!data?.ok || marcados.length === 0 || enviando}
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-sara-primary text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {enviando ? 'Enviando…' : `Responder de novo (${marcados.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
